'use client'

import { isNull } from 'lodash-es'
import { useTranslations } from 'next-intl'
import { Fragment } from 'react'

import { Slot, usePagination } from '@/shared/lib'
import {
  Chip,
  InnerLayout,
  OuterLayout,
  Pagination,
  Separator,
} from '@/shared/ui'

import { useArticles } from '../../../api/get-articles'
import { useCategory } from '../../../lib/category'
import { Campaign } from '../../list'

type Props = {
  pageType: 'host' | 'guest'
  categoryId: number | null
}

export const Articles = ({ pageType, categoryId }: Props) => {
  const t = useTranslations('magazine')
  const { page, handlePageChange } = usePagination()

  const { handleCategoryChange, selectedCategory } = useCategory()

  const { lastPage, articles, articleCategories } = useArticles({
    page,
    pageType,
    categoryId,
  })

  return (
    <OuterLayout>
      <InnerLayout className="mt-10 mb-15 flex flex-col">
        <ul className="mb-8.5 flex gap-1.5">
          <li>
            <Chip
              variant="solid"
              size="large"
              selected={isNull(selectedCategory)}
              onClick={handleCategoryChange(null)}
            >
              {t('tab.all')}
            </Chip>
          </li>
          {articleCategories?.map(({ id, name }) => (
            <li key={id}>
              <Chip
                variant="solid"
                size="large"
                selected={selectedCategory === id}
                onClick={handleCategoryChange(id)}
              >
                {name}
              </Chip>
            </li>
          ))}
        </ul>

        <div className="mb-10 grow">
          <ul className="grid grid-cols-2 gap-x-25">
            {articles.map(
              (
                {
                  id,
                  title,
                  description,
                  landingPageCode,
                  image,
                  categoryName,
                },
                index,
              ) => {
                const isEndOfRow = index % 2 === 1
                const notLastItem = index !== articles.length - 1

                return (
                  <Fragment key={id}>
                    <Campaign.Item
                      href={`/${pageType}/campaign/article/${landingPageCode}`}
                      size="small"
                      className="w-[490px]"
                      target="_blank"
                    >
                      <Slot name="image">{image}</Slot>
                      <Slot name="category">{categoryName}</Slot>
                      <Slot name="title">{title}</Slot>
                      <Slot name="subContent">{description}</Slot>
                    </Campaign.Item>

                    {isEndOfRow && notLastItem && (
                      <li
                        key={`separator-${index}`}
                        role="presentation"
                        aria-hidden="true"
                        className="col-span-2 my-5"
                      >
                        <Separator />
                      </li>
                    )}
                  </Fragment>
                )
              },
            )}
          </ul>
        </div>
        <Pagination
          current={page}
          total={lastPage}
          onPageChange={handlePageChange}
        />
      </InnerLayout>
    </OuterLayout>
  )
}
